<template>
    <DCard
        :title="__('settings.netsuite_currency_mapping.title')"
        :padding="true"
        class="my-3"
    >
        <p class="text-medium-emphasis">
            {{ __('settings.netsuite_currency_mapping.description') }}
        </p>

        <div
            v-if="errors['currency_mappings']"
            class="invalid-feedback d-block mb-3"
        >
            {{ errors['currency_mappings'] }}
        </div>

        <div class="netsuite-currency-mapping">
            <div
                v-for="(mapping, index) in rows"
                :key="`currency-mapping-${index}`"
                class="mapping-row"
            >
                <DRow class="align-items-center">
                    <DCol sm="5">
                        <DFormRow
                            :label="__('settings.netsuite_currency_mapping.currency_code')"
                            :error="errors[`currency_mappings.${index}.currency_code`]"
                            is-required
                            :hint="__('settings.netsuite_currency_mapping.currency_code_hint')"
                        >
                            <DSelect
                                v-model="mapping.currency_code"
                                :options="currencyCodeOptions"
                                is-required
                                :is-valid="isFieldValid(`currency_mappings.${index}.currency_code`)"
                            />
                        </DFormRow>
                    </DCol>

                    <DCol sm="5">
                        <DFormRow
                            :label="__('settings.netsuite_currency_mapping.netsuite_currency_id')"
                            :error="errors[`currency_mappings.${index}.netsuite_currency_id`]"
                            is-required
                            :hint="__('settings.netsuite_currency_mapping.netsuite_currency_id_hint')"
                        >
                            <DInput
                                v-model="mapping.netsuite_currency_id"
                                is-required
                                min="0"
                                max="2147483647"
                                :placeholder="__('settings.netsuite_currency_mapping.netsuite_currency_id_placeholder')"
                                :is-valid="isFieldValid(`currency_mappings.${index}.netsuite_currency_id`)"
                            />
                        </DFormRow>
                    </DCol>

                    <DCol
                        sm="2"
                        class="d-flex"
                    >
                        <DButton
                            v-if="rows.length > 1"
                            color="danger"
                            mini
                            icon="trash-can-outline"
                            @click="removeRow(index)"
                        />
                    </DCol>
                </DRow>
            </div>

            <DRow class="mt-3">
                <DCol>
                    <DButton
                        icon="plus"
                        :label="__('settings.netsuite_currency_mapping.add_mapping')"
                        variant="outline"
                        @click="addRow"
                    />
                </DCol>
            </DRow>
        </div>
    </DCard>
</template>

<script setup>
import { ref, watch } from 'vue'
import { __ } from '~@/Composables/translation'

const props = defineProps({
    initialMappings: {
        type: Array,
        default: () => []
    },
    currencyCodeOptions: {
        type: Array,
        default: () => []
    },
    errors: {
        type: Object,
        default: () => ({})
    }
})

const emit = defineEmits(['update:mappings'])

const rows = ref([])

function createEmptyRow () {
    return {
        currency_code: null,
        netsuite_currency_id: null
    }
}

function normalizeRows (items) {
    return items.map(item => ({
        currency_code: item.currency_code,
        netsuite_currency_id: item.netsuite_currency_id
    }))
}

function initializeRows () {
    rows.value = props.initialMappings.length > 0
        ? normalizeRows(props.initialMappings)
        : [createEmptyRow()]
}

initializeRows()

// Re-initialize from the server only when there are no validation errors,
// so a failed submission does not wipe the user's edits.
watch(
    () => props.initialMappings,
    () => {
        if (Object.keys(props.errors).length > 0) {
            return
        }

        initializeRows()
    }
)

// Keep the parent form in sync so the mappings are saved with the main NetSuite save.
watch(
    rows,
    () => {
        emit('update:mappings', normalizeRows(rows.value))
    },
    { deep: true, immediate: true }
)

function addRow () {
    rows.value.push(createEmptyRow())
}

function removeRow (index) {
    if (rows.value.length <= 1) {
        return
    }

    rows.value.splice(index, 1)
}

function isFieldValid (fieldPath) {
    return !props.errors[fieldPath]
}
</script>

<style scoped lang="scss">
.netsuite-currency-mapping {
    .mapping-row {
        margin-bottom: 1rem;

        &:not(:last-child) {
            padding-bottom: 1rem;
            border-bottom: 1px solid #e0e0e0;
        }
    }
}
</style>
